import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Controls } from '@xyflow/react';
import styled from 'styled-components';

import '@xyflow/react/dist/style.css';

import { EyeIcon } from 'assets/icons/eye-icon';
import { GraphIcon } from 'assets/icons/graph-icon';
import { PageLoader } from 'src/app/components/lib/loader';
import { MenuItem } from 'src/app/components/lib/menu-item';
import { Select } from 'src/app/components/lib/select';
import {
  NodeType,
  type WorkflowCampaignNode,
} from 'src/app/pages/marketing/workflows/workflow-campaign-builder/slides/workflow-editor/models';
import { StyledReactFlowEditor } from 'src/app/pages/marketing/workflows/workflow-campaign-builder/slides/workflow-editor/styles';
import {
  edgeTypes,
  nodeTypes,
} from 'src/app/pages/marketing/workflows/workflow-campaign-builder/slides/workflow-editor/utils';
import {
  useReadonlyWorkflowBuilderReducer,
  useWorkflowBuilderContext,
  WorkflowBuilderContext,
} from 'src/app/pages/marketing/workflows/workflow-campaign-builder/wizard/builder-context';
import { useDeliverabilityMetrics } from 'src/app/queries/marketing/get-deliverability-metrics';
import { useGetWorkflowCampaignQuery } from 'src/app/queries/marketing/get-workflow-campaign';
import { useGetWorkflowExecutionStats } from 'src/app/queries/marketing/get-workflow-execution-stats';

import { automatedDateDropdownOptions } from '../../../campaigns/utils/types';
import { WorkflowCampaignBuilderSummarySidebar } from '../../workflow-campaign-builder/summary-sidebar';

type WorkflowCampaignContentProps = {
  disableDrag?: boolean;
  marketingCampaignId: number;
};

export function WorkflowCampaignContent({ marketingCampaignId, disableDrag = false }: WorkflowCampaignContentProps) {
  const builderReducer = useReadonlyWorkflowBuilderReducer();

  return (
    <WorkflowBuilderContext.Provider value={builderReducer}>
      <WorkflowCampaignContentInternal disableDrag={disableDrag} marketingCampaignId={marketingCampaignId} />
    </WorkflowBuilderContext.Provider>
  );
}

function WorkflowCampaignContentInternal({ marketingCampaignId, disableDrag }: WorkflowCampaignContentProps) {
  const { data: workflowCampaign, isLoading } = useGetWorkflowCampaignQuery({ marketingCampaignId });

  const [builderState, dispatch] = useWorkflowBuilderContext();
  const { selectedNodeId, nodes, edges, isReadOnly } = builderState;
  const [showAnalytics, setShowAnalytics] = useState(true);
  const [timeframeInMonths, setTimeframeInMonths] = useState<number>(1);

  useEffect(() => {
    if (workflowCampaign) {
      dispatch({ type: 'update-full-campaign', payload: workflowCampaign });
    }
  }, [workflowCampaign, dispatch]);

  const fromDate = useMemo(() => {
    const days = timeframeInMonths * 30;
    return new Date(Date.now() - 1000 * 60 * 60 * 24 * days);
  }, [timeframeInMonths]);
  const toDate = useMemo(() => new Date(), []);

  const { data: workflowExecutionStats } = useGetWorkflowExecutionStats(builderState.marketingCampaignId, fromDate);

  const statsMap = useMemo(() => {
    const result = new Map(workflowExecutionStats?.map((stat) => [stat.StepId, stat]));
    const triggerEdge = edges.find((edge) => edge.source === '-1');
    result.set(-1, {
      TimesEntered: result.get(Number(triggerEdge?.target))?.TimesEntered ?? 0,
      TimesErrored: 0,
      Interruptions: [],
      StepId: -1,
    });
    return result;
  }, [workflowExecutionStats, edges]);

  const { data: deliverabilityMetrics, isLoading: isDeliverabilityMetricsLoading } = useDeliverabilityMetrics(
    builderState.marketingCampaignId,
    fromDate,
    toDate
  );

  const getNodeDeliverabilityStats = useCallback(
    (node: WorkflowCampaignNode) => {
      const { data } = node;
      if (!deliverabilityMetrics || data.nodeType !== NodeType.ACTION) {
        return null;
      }

      if (data.actionType === 'sendEmail') {
        return deliverabilityMetrics.EmailData?.find(
          (e) => e.MarketingEmailId === data.config.emailConfig?.marketingEmailId
        );
      }
      if (data.actionType === 'sendSMS') {
        return deliverabilityMetrics.SMSData?.find((s) => s.MarketingSMSId === data.config.smsConfig?.marketingSMSId);
      }
      if (data.actionType === 'sendPushNotification') {
        return deliverabilityMetrics.PushData?.find(
          (p) => p.MarketingPushId === data.config.pushConfig?.marketingPushId
        );
      }
      return null;
    },
    [deliverabilityMetrics]
  );

  const setSelectedNode = useCallback(
    (nodeId: string, isNewNode) =>
      dispatch({
        type: 'set-selected-node',
        payload: {
          id: nodeId,
          isNewNode,
        },
      }),
    [dispatch]
  );

  const handleOnNodeClick = useCallback(
    (event, node) => {
      if (selectedNodeId !== node.id) {
        setSelectedNode(node.id, false);
      }
    },
    [setSelectedNode, selectedNodeId]
  );

  const nodesDerivedWithProps = useMemo(
    () =>
      nodes?.map((node) => ({
        ...node,
        data: {
          ...node.data,
          stats: statsMap.get(Number(node.id)),
          deliverabilityStats: getNodeDeliverabilityStats(node),
          isDeliverabilityMetricsLoading,
          showAnalytics,
        },
      })) as WorkflowCampaignNode[],
    [nodes, statsMap, getNodeDeliverabilityStats, isDeliverabilityMetricsLoading, showAnalytics]
  );

  const edgesDerivedWithProps = useMemo(
    () =>
      edges?.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        type: edge.edgeType,
      })),
    [edges]
  );

  return isLoading ? (
    <LoaderContainer>
      <PageLoader />
    </LoaderContainer>
  ) : (
    <WorkflowContainer>
      <AnalyticsHeaderRow>
        <AnalyticsToggleContainer>
          <AnalyticsToggleButton onClick={() => setShowAnalytics(!showAnalytics)}>
            <GraphIcon />
            {showAnalytics ? 'Hide analytics' : 'Show analytics'}
          </AnalyticsToggleButton>
        </AnalyticsToggleContainer>
        <TimeframeSelectContainer>
          <StyledSelect
            automationId='date-campaign-node-analytics-timeframe-dropdown'
            id='date-campaign-node-analytics-timeframe-dropdown'
            value={timeframeInMonths}
            onChange={({ target }) => setTimeframeInMonths(Number(target.value))}
          >
            {automatedDateDropdownOptions.map((option: { id: string; label: string; value: number }) => (
              <MenuItem
                automationId={`date-campaign-node-analytics-timeframe-dropdown-${option.id}`}
                key={option.value}
                value={option.value}
              >
                {option.label}
              </MenuItem>
            ))}
          </StyledSelect>
        </TimeframeSelectContainer>
      </AnalyticsHeaderRow>
      <FlowContainer>
        {isReadOnly && (
          <ReadOnlyBadge>
            <EyeIcon size='12px' />
            <span>Read only</span>
          </ReadOnlyBadge>
        )}
        <StyledReactFlowEditor
          edges={edgesDerivedWithProps}
          edgeTypes={edgeTypes}
          fitView
          nodes={nodesDerivedWithProps}
          nodesDraggable={false}
          nodeTypes={nodeTypes}
          panOnDrag={!disableDrag}
          onNodeClick={handleOnNodeClick}
        >
          <Controls />
        </StyledReactFlowEditor>
        {selectedNodeId && <WorkflowCampaignBuilderSummarySidebar />}
      </FlowContainer>
    </WorkflowContainer>
  );
}

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const WorkflowContainer = styled.div`
  width: 100%;
  display: flex;
  flex-grow: 2;
  overflow-y: auto;
  flex-direction: column;
`;

const FlowContainer = styled.div`
  flex: 1;
  background: #fff;
  width: 100%;
  display: flex;
  flex-direction: row;
  position: relative;
`;

const AnalyticsHeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0;
  margin-bottom: 0;
`;

const AnalyticsToggleContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  padding: --var(--sizes-40);
  padding-left: 0;
`;

const AnalyticsToggleButton = styled.button`
  background: var(--color-gray-20);
  align-items: center;
  color: #222;
  border-radius: var(--sizes-20);
  padding: var(--sizes-30) var(--sizes-50);
  display: flex;
  gap: var(--sizes-20);
  font: var(--font-regular-14pt-semibold);
  line-height: var(--sizes-50);
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: var(--color-gray-40);
  }

  &:focus {
    outline: none;
  }
`;

const ReadOnlyBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--sizes-20);
  background: var(--color-purple-10);
  color: var(--color-purple-100);
  font: var(--font-extra-small-12pt-semibold);
  border-radius: var(--sizes-50);
  padding: var(--sizes-10) var(--sizes-30);
  position: absolute;
  top: var(--sizes-40);
  left: var(--sizes-40);
  z-index: 2;
  box-shadow: none;
  user-select: none;
`;
const TimeframeSelectContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 1;
`;

const StyledSelect = styled(Select)`
  width: auto;
  display: inline-flex;
  margin: 0 var(--sizes-40) var(--sizes-40) 0;

  .MuiInputBase-input {
    padding-right: var(--sizes-90) !important;
  }
`;
